import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setSectionData} from "../store/reducers/sectionReducer";
import sectionService from "../services/SectionService";
import {Spin, Row, Col, message} from "antd";
import {
    CheckOutlined, CreditCardOutlined,
    EnvironmentOutlined, FileOutlined,
    UserAddOutlined,
    UsergroupAddOutlined,
} from "@ant-design/icons";
import theme from "../theme";
import {PieChart, Pie, Cell} from "recharts";
import {motion} from "framer-motion";
import {useMediaQuery} from "react-responsive";
import PmBigButton from "../components/PmBigButton";
import {useNavigate} from "react-router-dom";
import statisticsService from "../services/StatisticsService";
import {
    COT_ROLE,
    PROMOTER_ROLE,
    QUERY_STATE_ROLE,
    SECTIONAL_ROLE,
    STATE_ROLE,
    useUserRole
} from "../utils/userSessionUtils";

const OdometerGauge = ({value, maxValue}) => {
    const data = [
        {name: "Filled", value},
        {name: "Remaining", value: value > maxValue ? 0 : maxValue - value},
    ];
    const COLORS = [theme.colors.secondary, "#e8e8e8"];

    return (
        <div className="flex flex-col items-center w-full">
            <p style={{fontSize: "1.2rem", fontWeight: "bold", marginTop: "8px"}}>
                Tienes un avance de {value} / {maxValue} convencidos
            </p>
            <PieChart width={300} height={130} className="w-full max-w-[300px]">
                <Pie
                    data={data}
                    cx="50%"
                    cy="100%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={80}
                    outerRadius={120}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]}/>
                    ))}
                </Pie>
            </PieChart>
        </div>
    );
};

const Home = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const isMobile = useMediaQuery({maxWidth: 800});
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const section = useSelector((state) => state.section);
    const navigate = useNavigate();
    const [statistics, setStatistics] = useState(null);
    const role = useUserRole();
    const auth = useSelector((state) => state.auth || {});
    const entityId = auth.user?.entityId || 0;
    const allowedEntities = [10];

    useEffect(() => {
        const fetchSectionData = async () => {
            try {
                if (role !== STATE_ROLE && role !== QUERY_STATE_ROLE) {
                    const response = await sectionService.getAll();
                    dispatch(setSectionData(response.data));
                }
            } catch (error) {
                console.error("Error fetching section data:", error);
                messageApi.error("Error al cargar los datos de la sección");
            }
        };

        const fetchStatistics = async () => {
            try {
                const responseStatistics = await statisticsService.getStatisticsByUser();
                setStatistics(responseStatistics.data);
            } catch (error) {
                console.error("Error fetching statistics:", error);
                messageApi.error("Error al cargar las estadísticas");
            }
        };

        const loadData = async () => {
            await Promise.all([fetchSectionData(), fetchStatistics()]);
            setLoading(false);
        };

        loadData();
    }, [dispatch, role, setStatistics, messageApi]);

    return (
        <>
            {contextHolder}
            <motion.div
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.5}}
            style={{
                maxWidth: isMobile ? "100%" : "90%",
                width: "90vw",
                margin: "20px auto",
                background: theme.colors.card,
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                overflowX: "auto",
            }}
        >
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Spin size="large"/>
                    </div>
                ) : (
                    <div className="flex flex-col space-y-6">
                        <Row justify="space-between" gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={24} align="center">
                                {(statistics != null) &&
                                    <OdometerGauge value={statistics.data.advance} maxValue={statistics.data.goal}/>
                                }
                            </Col>
                            {
                                (role !== STATE_ROLE && role !== QUERY_STATE_ROLE) &&
                                <Col xs={24} sm={24} md={24} align="center">
                                    <p style={{fontSize: "1.2rem", fontWeight: "bold", marginTop: "8px"}}>
                                        <EnvironmentOutlined
                                            className="mr-2"/> {section.municipalityName}, {section.entityName}
                                    </p>
                                </Col>
                            }
                        </Row>
                        <Row justify="space-between" gutter={[16, 16]}
                             style={{maxWidth: "480px", margin: "0 auto"}}>
                            {(role === SECTIONAL_ROLE || role === PROMOTER_ROLE) &&
                                <Col xs={12} sm={12} md={11}>
                                    <PmBigButton
                                        hidden
                                        text={"Registro"}
                                        icon={<UserAddOutlined
                                            style={{fontSize: "48px", marginBottom: "24px", color: "white"}}/>}
                                        onClick={() => {
                                            navigate("/registro");
                                        }}
                                    />
                                </Col>
                            }
                            {(role === SECTIONAL_ROLE || role === PROMOTER_ROLE) &&
                                <Col xs={12} sm={12} md={11}>
                                    <PmBigButton
                                        hidden
                                        text={"Convencidos"}
                                        icon={<UsergroupAddOutlined
                                            style={{fontSize: "48px", marginBottom: "24px", color: "white"}}/>}
                                        onClick={() => {
                                            navigate("/personas");
                                        }}
                                    />
                                </Col>
                            }
                            {(role === SECTIONAL_ROLE || role === COT_ROLE) &&
                                <Col xs={12} sm={12} md={11}>
                                    <PmBigButton
                                        hidden
                                        text={"Promotores"}
                                        icon={<CheckOutlined
                                            style={{fontSize: "48px", marginBottom: "24px", color: "white"}}/>}
                                        onClick={() => {
                                            navigate("/promotores")
                                        }}
                                    />
                                </Col>
                            }
                            {(role === SECTIONAL_ROLE) &&
                                <Col xs={12} sm={12} md={11}>
                                    <PmBigButton
                                        hidden
                                        text={"Información de nómina"}
                                        icon={<CreditCardOutlined
                                            style={{fontSize: "48px", marginBottom: "24px", color: "white"}}/>}
                                        onClick={() => {
                                            navigate("/informacion-nomina")
                                        }}
                                    />
                                </Col>
                            }
                            {(role === SECTIONAL_ROLE && allowedEntities.includes(entityId)) &&
                                <Col xs={12} sm={12} md={11}>
                                    <PmBigButton
                                        text={"Directorio"}
                                        icon={<FileOutlined
                                            style={{fontSize: "48px", marginBottom: "24px", color: "white"}}/>}
                                        onClick={() => {
                                            navigate("/informacion-nomina")
                                        }}
                                    />
                                </Col>
                            }
                        </Row>
                    </div>
                )}
            </div>
        </motion.div>
        </>
    );
};

export default Home;